import { AppPageHeader, InfiniteTable } from '@/components/_common';
import { useItemServiceList } from '@/core/domain/workorder/ItemService/ItemService.hooks';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { useCustomerVehicle } from '@/core/domain/people/CustomerVehicles/CustomerVehicles.hooks';
import { useMemo, useState } from 'react';
import {
  ItemServiceExecuted,
  ItemServiceListRequest,
} from '@/core/domain/workorder/ItemService/ItemService.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Divider } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import formatterPrice from '@/utils/number/formatterPrice';
import formatterPercent from '@/utils/number/formatterPercent';
import { useWorkorderItems } from '..';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { v4 } from 'uuid';
import { useWorkOrderItemsVerify } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';

const ServiceListExecuted: React.FC = () => {
  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { items, setItems } = useWorkorderItems();

  const { data: workorderItemsData } = useWorkOrderItemsVerify(complement?.id);
  const workorderItems = workorderItemsData?.data || [];

  const { data: vehicle } = useCustomerVehicle(
    workorder?.customer?.id,
    workorder?.vehicle.id,
  );

  const [params, setParams] = useState<ItemServiceListRequest>();

  const servicesData = useItemServiceList({
    ...params,
    modelId: vehicle?.model.id,
  });

  const services = flatInfiniteData(servicesData.data);

  const minDiscountPolicy = useMemo(() => {
    if (
      isAppPortocar &&
      !workorder?.partnerDiscount &&
      workorder?.employeeDiscount
    )
      return 10;

    if (
      !isAppPortocar ||
      !complement?.policyNumber ||
      !workorder?.partnerDiscount
    )
      return undefined;

    const isPortoPolicy =
      !!complement?.policyNumber &&
      complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

    return isPortoPolicy ? 20 : 15;
  }, [
    isAppPortocar,
    complement?.policyNumber,
    complement?.companyCode,
    workorder?.partnerDiscount,
  ]);

  const columns: ColumnsType<ItemServiceExecuted> = [
    { title: 'descrição', dataIndex: 'description', width: 200 },
    {
      title: 'Cód. Tempário',
      dataIndex: 'temparioCode',
      render: (value) => value || '-',
      width: 150,
    },
    {
      title: 'Sub área',
      dataIndex: ['subArea', 'name'],
      render: (value) => value || '-',
      width: 150,
    },
    {
      align: 'right',
      title: 'Preço unitário',
      dataIndex: 'unitPrice',
      render: (value) => formatterPrice(value || 0),
      width: 150,
    },
    {
      align: 'right',
      title: 'Tempo/Unidade',
      width: 150,
      render: (_, row) => `${row.quantity}/${row.unit || '-'}`,
    },
    {
      align: 'right',
      title: 'Desconto',
      dataIndex: 'discount',
      render: (value) =>
        typeof value === 'number' ? formatterPercent(value) : '-',
      width: 150,
    },
    {
      align: 'right',
      title: 'Preço de custo',
      dataIndex: 'unitCost',
      render: (value) => formatterPrice(value || 0),
      width: 150,
    },
    {
      align: 'right',
      title: 'Total',
      render: (_, row) => {
        const subTotal = row.quantity * row.unitPrice;
        const result = subTotal - subTotal * (row.discount || 0);

        return <strong>{formatterPrice(result)}</strong>;
      },
      width: 150,
    },
  ];

  return (
    <>
      <AppPageHeader
        searchValue={params?.search}
        onSearch={(search) => setParams((prev) => ({ ...prev, search }))}
      />

      <p>
        Veículo:{' '}
        <strong>{`${workorder?.vehicle.model} - ${workorder?.vehicle.brand}`}</strong>
      </p>

      <Divider />

      <InfiniteTable<ItemServiceExecuted>
        dataSource={services.map((item) => ({ ...item, id: v4() }))}
        loading={servicesData.isLoading}
        isFetching={servicesData.isFetching}
        hasNextPage={servicesData.hasNextPage}
        fetchNextPage={servicesData.fetchNextPage}
        columns={columns}
        rowKey={(row) => row.workOrderItemId}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: items.map((item) => item.itemId),
          onChange: (_, rows) => {
            const discount = minDiscountPolicy;

            if (complement) {
              setItems(
                rows.map(
                  ({
                    subArea,
                    taskId: _,
                    workOrderItemId,
                    description,
                    ...item
                  }) => {
                    const unitPrice = item.unitPrice;

                    const discountValue =
                      typeof discount === 'number'
                        ? ((item.quantity || 0) *
                            (unitPrice || 0) *
                            (discount || 0)) /
                          100
                        : undefined;

                    return {
                      ...item,
                      discount,
                      discountValue,
                      complementId: complement?.id,
                      itemType: WorkOrderItemType.Service,
                      itemId: workOrderItemId,
                      itemName: description,
                      subAreaId: subArea.id,
                    };
                  },
                ),
              );
            }
          },
          getCheckboxProps: (item) => ({
            disabled: !!workorderItems.find(
              (workorderItem) =>
                workorderItem.type === WorkOrderItemType.Service &&
                item?.taskId === workorderItem?.itemId,
            ),
          }),
        }}
      />
    </>
  );
};

export default ServiceListExecuted;
